import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/useAuth';
import { useI18n } from '../../context/useI18n';
import { resolveOrganizationBrandName } from '../../utils/branding';
import MastersBackHeader from '../common/MastersBackHeader';
import { detectVideoPlatform, videoUrlToEmbed } from '../../utils/videoEmbed';
import {
  liveClassTypeOptions,
  resolveGalleryClassType,
} from '../../constants/liveClassTypes';
import {
  resolveCycleEnd,
  todayCycleStr,
} from '../../utils/cycleUtils';
import './AdminD28dClassGallery.css';

const emptyForm = () => ({
  title: '',
  description: '',
  video_url: '',
  program_id: '',
  class_type: '',
  coach: '',
  class_date: '',
});

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('es-CO', { dateStyle: 'medium' }).format(new Date(value));
  } catch {
    return value;
  }
}

/** Nombres en minúsculas / title-case (sin ALL CAPS) para alinear con otras tarjetas. */
function formatCoachName(name) {
  if (!name) return '—';
  const lower = String(name).trim().toLowerCase();
  if (!lower) return '—';
  return lower.replace(/(^|\s)\S/g, (ch) => ch.toUpperCase());
}

function activeCyclesOnly(list = []) {
  const today = todayCycleStr();
  const open = list.filter((c) => {
    if (c?.is_active_today) return true;
    const end = resolveCycleEnd(c);
    if (!end) return false;
    return String(end).slice(0, 10) >= today;
  });
  return open.length ? open : list;
}

function VideoPreviewModal({ item, onClose }) {
  const embed = videoUrlToEmbed(item?.video_url);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="training-modal-panel surface-readable bg-[var(--d28d-surface)] rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--d28d-border)] shrink-0">
          <h3 className="font-semibold text-[var(--d28d-text)] truncate pr-2">{item?.title}</h3>
          <button type="button" className="btn-secondary text-sm shrink-0" onClick={onClose}>Cerrar</button>
        </div>
        <div className="p-4 overflow-y-auto">
          {embed ? (
            <div className="relative w-full rounded-xl overflow-hidden bg-black aspect-video max-h-[60vh] mx-auto">
              <iframe
                title={item?.title}
                src={embed}
                className="absolute inset-0 w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <p className="text-sm text-stone-600 mb-3">No se puede incrustar este enlace.</p>
          )}
          {item?.video_url ? (
            <a href={item.video_url} target="_blank" rel="noopener noreferrer" className="text-sm text-lime-700 hover:underline break-all">
              {item.video_url}
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function AdminD28dClassGallery({
  onBack,
  readOnly = false,
  scopeGymId = null,
  scopeGymName = '',
}) {
  const { user } = useAuth();
  const { t } = useI18n();
  const isGymScoped = scopeGymId != null && scopeGymId !== '';
  const [items, setItems] = useState([]);
  const [pastClasses, setPastClasses] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [resolvedGymName, setResolvedGymName] = useState(scopeGymName || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [previewItem, setPreviewItem] = useState(null);
  const [search, setSearch] = useState('');
  const [classTypeFilter, setClassTypeFilter] = useState('');
  const [cycleFilter, setCycleFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [cycles, setCycles] = useState([]);
  const cycleUserPickedRef = useRef(false);
  const [linkingClassId, setLinkingClassId] = useState(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());

  const isPlatform = useMemo(() => {
    const roles = Array.isArray(user?.roles) && user.roles.length ? user.roles : [user?.rol].filter(Boolean);
    return roles.some((r) => ['super_admin', 'admin_d28d'].includes(r));
  }, [user]);

  const viewQueryParams = useMemo(() => {
    const params = {};
    if (scopeGymId) params.gym_id = scopeGymId;
    if (cycleFilter) params.cycle_id = cycleFilter;
    if (yearFilter) params.year = yearFilter;
    if (monthFilter) params.month = monthFilter;
    return params;
  }, [scopeGymId, cycleFilter, yearFilter, monthFilter]);

  const fetchGallery = useCallback(async () => {
    try {
      const endpoint = readOnly ? '/live-classes/class-gallery' : '/live-classes/admin/class-gallery';
      const res = await api.get(endpoint, { params: viewQueryParams });
      if (res.data?.success) setItems(res.data.data || []);
    } catch {
      setError(t('d28d.gallery.load_error', 'Error cargando las grabaciones de clase'));
    }
  }, [readOnly, viewQueryParams, t]);

  const fetchCycles = useCallback(async () => {
    try {
      const params = scopeGymId
        ? { gym_id: scopeGymId, gym_only: 'true' }
        : { platform_only: 'true' };
      const res = await api.get('/cycles', { params });
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      const active = activeCyclesOnly(list);
      setCycles(active);
      if (!cycleUserPickedRef.current) {
        // Por defecto: todos los ciclos (vistas totales). El filtro de ciclo sigue disponible.
        setCycleFilter('');
      } else {
        setCycleFilter((prev) => (
          !prev || active.some((c) => String(c.id) === String(prev))
            ? prev
            : ''
        ));
      }
    } catch {
      setCycles([]);
    }
  }, [scopeGymId]);

  const recordView = useCallback(async (item) => {
    if (!item?.id) return;
    try {
      const res = await api.post(`/live-classes/class-gallery/${item.id}/view`);
      const count = res.data?.data?.view_count;
      if (typeof count === 'number') {
        setItems((prev) => prev.map((row) => (
          Number(row.id) === Number(item.id) ? { ...row, view_count: count } : row
        )));
      }
    } catch {
      /* no bloquear preview si falla el contador */
    }
  }, []);

  const openPreview = useCallback((item) => {
    setPreviewItem(item);
    recordView(item);
  }, [recordView]);

  const fetchPastClasses = useCallback(async () => {
    if (readOnly) return;
    try {
      const params = scopeGymId ? { gym_id: scopeGymId } : {};
      const res = await api.get('/live-classes/admin', { params });
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      const now = Date.now();
      setPastClasses(rows.filter((c) => new Date(c.end_time || c.start_time).getTime() < now));
    } catch {
      setPastClasses([]);
    }
  }, [readOnly, scopeGymId]);

  const fetchPrograms = useCallback(async () => {
    if (!isGymScoped) {
      setPrograms([]);
      return;
    }
    try {
      const res = await api.get('/programs', { params: { gym_id: scopeGymId, gym_only: 'true' } });
      setPrograms(res.data?.data || []);
    } catch {
      setPrograms([]);
    }
  }, [isGymScoped, scopeGymId]);

  useEffect(() => {
    setResolvedGymName(scopeGymName || '');
  }, [scopeGymName]);

  useEffect(() => {
    cycleUserPickedRef.current = false;
    setCycleFilter('');
  }, [scopeGymId]);

  const fetchGymName = useCallback(async () => {
    if (!isGymScoped || scopeGymName) return;
    try {
      const res = await api.get(`/gyms/${scopeGymId}`);
      const g = res.data?.data || res.data;
      const name = resolveOrganizationBrandName({ entity: g });
      if (name) setResolvedGymName(name);
    } catch {
      /* optional */
    }
  }, [isGymScoped, scopeGymId, scopeGymName]);

  useEffect(() => {
    fetchGallery();
    fetchPastClasses();
    fetchPrograms();
    fetchGymName();
    fetchCycles();
  }, [fetchGallery, fetchPastClasses, fetchPrograms, fetchGymName, fetchCycles]);

  const classTypeFilterOptions = useMemo(
    () => liveClassTypeOptions(isGymScoped),
    [isGymScoped],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      if (classTypeFilter) {
        const type = resolveGalleryClassType(item, { gymScoped: isGymScoped });
        if (type !== classTypeFilter) return false;
      }
      if (!q) return true;
      return [item.title, item.coach, item.class_type, item.program_id, item.description]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [items, search, classTypeFilter, isGymScoped]);

  const galleryLinkedClassIds = useMemo(
    () => new Set(items.filter((i) => i.live_class_id != null).map((i) => Number(i.live_class_id))),
    [items],
  );

  const pendingPastClasses = useMemo(
    () => pastClasses.filter((c) => !galleryLinkedClassIds.has(Number(c.id))),
    [pastClasses, galleryLinkedClassIds],
  );

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return [current, current - 1, current - 2];
  }, []);

  const monthOptions = useMemo(() => ([
    { value: '1', label: t('common.month_1', 'Enero') },
    { value: '2', label: t('common.month_2', 'Febrero') },
    { value: '3', label: t('common.month_3', 'Marzo') },
    { value: '4', label: t('common.month_4', 'Abril') },
    { value: '5', label: t('common.month_5', 'Mayo') },
    { value: '6', label: t('common.month_6', 'Junio') },
    { value: '7', label: t('common.month_7', 'Julio') },
    { value: '8', label: t('common.month_8', 'Agosto') },
    { value: '9', label: t('common.month_9', 'Septiembre') },
    { value: '10', label: t('common.month_10', 'Octubre') },
    { value: '11', label: t('common.month_11', 'Noviembre') },
    { value: '12', label: t('common.month_12', 'Diciembre') },
  ]), [t]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.title?.trim() || !form.video_url?.trim()) return;
    setLoading(true);
    setError('');
    try {
      await api.post('/live-classes/admin/class-gallery', {
        ...form,
        title: form.title.trim(),
        video_url: form.video_url.trim(),
        program_id: form.program_id?.trim() || null,
        class_date: form.class_date || null,
        ...(scopeGymId ? { gym_id: Number(scopeGymId), is_global: false } : {}),
      });
      await fetchGallery();
      setForm(emptyForm());
    } catch (err) {
      setError(err?.response?.data?.error || 'Error guardando video');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingItem?.id || !editForm.title?.trim() || !editForm.video_url?.trim()) return;
    setLoading(true);
    setError('');
    try {
      await api.put(`/live-classes/admin/class-gallery/${editingItem.id}`, {
        ...editForm,
        title: editForm.title.trim(),
        video_url: editForm.video_url.trim(),
        program_id: editForm.program_id?.trim() || null,
        class_date: editForm.class_date || null,
      });
      setEditingItem(null);
      setEditForm(emptyForm());
      await fetchGallery();
    } catch (err) {
      setError(err?.response?.data?.error || 'Error actualizando video');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setEditForm({
      title: item.title || '',
      description: item.description || '',
      video_url: item.video_url || '',
      program_id: item.program_id || '',
      class_type: item.class_type || '',
      coach: item.coach || '',
      class_date: item.class_date ? String(item.class_date).slice(0, 10) : '',
    });
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este video de la galería?')) return;
    try {
      await api.delete(`/live-classes/admin/class-gallery/${id}`);
      await fetchGallery();
    } catch {
      setError('Error eliminando video');
    }
  };

  const handleLinkFromClass = async (liveClassId) => {
    if (!linkUrl.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.post(`/live-classes/admin/class-gallery/from-live-class/${liveClassId}`, {
        video_url: linkUrl.trim(),
      });
      const created = res.data?.data;
      if (created?.id) {
        setItems((prev) => {
          const without = prev.filter((i) => Number(i.id) !== Number(created.id));
          return [created, ...without];
        });
      }
      setLinkingClassId(null);
      setLinkUrl('');
      await Promise.all([fetchGallery(), fetchPastClasses()]);
    } catch (err) {
      setError(err?.response?.data?.error || 'Error vinculando grabación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-main-view space-y-6">
      <header className="dashboard-header">
        {onBack && isGymScoped ? (
          <MastersBackHeader
            onBack={onBack}
            title={t('gym.gallery.title', 'GRABACIONES DE CLASE de tu sede')}
          />
        ) : onBack ? (
          <button type="button" className="d28d-gallery-btn d28d-gallery-btn--edit mb-2" onClick={onBack}>
            {t('panel.back_services', '← Servicios')}
          </button>
        ) : null}
        {!isGymScoped || !onBack ? (
          <h2 className="d28d-page-title">
            {isGymScoped
              ? t('gym.gallery.title', 'GRABACIONES DE CLASE de tu sede')
              : t('d28d.gallery.title', 'GRABACIONES DE CLASE')}
          </h2>
        ) : null}
        <p className="d28d-text-muted text-sm mt-1">
          {isGymScoped
            ? t(
              'gym.gallery.subtitle',
              'Grabaciones y enlaces de las clases en vivo de tu gimnasio virtual. Solo visible para usuarios de tu sede.',
            )
            : t(
              'd28d.gallery.subtitle',
              'Grabaciones y enlaces de clases en vivo ya realizadas. Independiente de la biblioteca de ejercicios de entrenadores.',
            )}
        </p>
        {isGymScoped && resolvedGymName ? (
          <p className="text-sm d28d-text-muted mt-1">
            <strong className="text-[var(--d28d-text)]">{resolvedGymName}</strong>
          </p>
        ) : null}
      </header>

      {error ? <div className="alert-error text-sm">{error}</div> : null}

      {!readOnly ? (
      <form onSubmit={handleAdd} className="card p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="label">Título de la clase</label>
          <input
            className="input mt-1 w-full"
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={isGymScoped ? 'Ej: Fuerza — Lunes 08:00' : 'Vital D28D — Fuerza Lunes'}
          />
        </div>
        <div className="md:col-span-2">
          <label className="label">URL del video (YouTube, Vimeo, Zoom recording…)</label>
          <input className="input mt-1 w-full" required type="url" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://..." />
        </div>
        <div>
          <label className="label">{t('common.program', 'Programa')}</label>
          {isGymScoped ? (
            programs.length > 0 ? (
              <select
                className="input mt-1 w-full"
                value={form.program_id}
                onChange={(e) => setForm({ ...form, program_id: e.target.value })}
              >
                <option value="">{t('common.select', 'Seleccionar…')}</option>
                {programs.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-amber-700 mt-1 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {t('gym.gallery.need_program', 'Crea un programa en Maestros → Programas antes de publicar grabaciones.')}
              </p>
            )
          ) : (
            <input
              className="input mt-1 w-full"
              value={form.program_id}
              onChange={(e) => setForm({ ...form, program_id: e.target.value })}
              placeholder="vital, pancitas…"
            />
          )}
        </div>
        <div>
          <label className="label">Fecha de la clase</label>
          <input className="input mt-1 w-full" type="date" value={form.class_date} onChange={(e) => setForm({ ...form, class_date: e.target.value })} />
        </div>
        <div>
          <label className="label">{t('schedule.class_type', 'Tipo de clase')}</label>
          <select
            className="input mt-1 w-full"
            value={form.class_type}
            onChange={(e) => setForm({ ...form, class_type: e.target.value })}
          >
            <option value="">{t('schedule.select_class_type', 'Selecciona tipo de clase')}</option>
            {classTypeFilterOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Coach / anfitrión</label>
          <input className="input mt-1 w-full" value={form.coach} onChange={(e) => setForm({ ...form, coach: e.target.value })} />
        </div>
        <div className="md:col-span-2">
          <label className="label">Notas (opcional)</label>
          <textarea className="input mt-1 w-full min-h-[72px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="md:col-span-2">
          <button type="submit" className="d28d-gallery-btn d28d-gallery-btn--view" disabled={loading}>
            {t('d28d.gallery.publish', '+ Publicar grabación')}
          </button>
        </div>
      </form>
      ) : null}

      {!readOnly && editingItem ? (
        <form onSubmit={handleUpdate} className="card p-4 grid grid-cols-1 md:grid-cols-2 gap-4 border-2 border-lime-300">
          <div className="md:col-span-2 flex items-center justify-between gap-2">
            <h3 className="font-semibold text-[var(--d28d-text)]">Editar grabación</h3>
            <button type="button" className="d28d-gallery-btn d28d-gallery-btn--edit" onClick={() => { setEditingItem(null); setEditForm(emptyForm()); }}>Cancelar</button>
          </div>
          <div className="md:col-span-2">
            <label className="label">Título</label>
            <input className="input mt-1 w-full" required value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className="label">URL del video</label>
            <input className="input mt-1 w-full" required type="url" value={editForm.video_url} onChange={(e) => setEditForm({ ...editForm, video_url: e.target.value })} />
          </div>
          <div>
            <label className="label">Fecha</label>
            <input className="input mt-1 w-full" type="date" value={editForm.class_date} onChange={(e) => setEditForm({ ...editForm, class_date: e.target.value })} />
          </div>
          <div>
            <label className="label">Coach</label>
            <input className="input mt-1 w-full" value={editForm.coach} onChange={(e) => setEditForm({ ...editForm, coach: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <button type="submit" className="d28d-gallery-btn d28d-gallery-btn--view" disabled={loading}>Guardar cambios</button>
          </div>
        </form>
      ) : null}

      {!readOnly && pendingPastClasses.length > 0 ? (
        <section className="card p-4 space-y-3">
          <h3 className="font-semibold text-[var(--d28d-text)]">Vincular desde clases pasadas</h3>
          <p className="text-sm d28d-text-muted">Selecciona una clase ya realizada y pega la URL de la grabación.</p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {pendingPastClasses.slice(0, 20).map((cls) => (
              <div key={cls.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-xl bg-[var(--d28d-surface)] border border-[var(--d28d-border)]">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[var(--d28d-text)] truncate">{cls.title}</p>
                  <p className="text-xs d28d-text-muted normal-case">{formatDate(cls.end_time || cls.start_time)} · {formatCoachName(cls.coach)}</p>
                </div>
                {linkingClassId === cls.id ? (
                  <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <input className="input text-sm" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="URL grabación" />
                    <button type="button" className="btn-primary text-sm" disabled={loading} onClick={() => handleLinkFromClass(cls.id)}>Guardar</button>
                    <button type="button" className="btn-secondary text-sm" onClick={() => { setLinkingClassId(null); setLinkUrl(''); }}>Cancelar</button>
                  </div>
                ) : (
                  <button type="button" className="btn-secondary text-sm whitespace-nowrap" onClick={() => { setLinkingClassId(cls.id); setLinkUrl(''); }}>
                    + URL grabación
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <input
            className="input flex-1"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('d28d.gallery.search', 'Buscar por título, coach o programa…')}
          />
          <select
            className="input sm:max-w-[220px]"
            value={classTypeFilter}
            onChange={(e) => setClassTypeFilter(e.target.value)}
            aria-label={t('schedule.filter_class_type', 'Filtrar por tipo de clase')}
          >
            <option value="">{t('schedule.all_class_types', 'Todos los tipos de clase')}</option>
            {classTypeFilterOptions.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <span className="text-sm d28d-text-muted shrink-0">{filtered.length} video(s)</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <select
            className="input sm:max-w-[240px]"
            value={cycleFilter}
            onChange={(e) => {
              cycleUserPickedRef.current = true;
              setCycleFilter(e.target.value);
            }}
            aria-label={t('d28d.gallery.filter_cycle', 'Filtrar visualizaciones por ciclo')}
          >
            <option value="">{t('d28d.gallery.all_cycles', 'Todos los ciclos')}</option>
            {cycles.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || `Ciclo ${c.id}`}{c.label ? ` — ${c.label}` : ''}
                {c.is_active_today ? ' · actual' : ''}
              </option>
            ))}
          </select>
          <select
            className="input sm:max-w-[160px]"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            aria-label={t('d28d.gallery.filter_month', 'Mes de visualizaciones')}
          >
            <option value="">{t('d28d.gallery.all_months', 'Todos los meses')}</option>
            {monthOptions.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <select
            className="input sm:max-w-[140px]"
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            aria-label={t('d28d.gallery.filter_year', 'Año de visualizaciones')}
          >
            <option value="">{t('d28d.gallery.all_years', 'Todos los años')}</option>
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <span className="text-xs d28d-text-muted">
            {t('d28d.gallery.views_filter_hint', 'Los filtros aplican al contador de visualizaciones.')}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto card">
        <table className="w-full text-left">
          <thead className="bg-[var(--d28d-surface)]">
            <tr>
              <th className="px-3 py-3 text-xs font-semibold d28d-text-muted uppercase">Clase</th>
              <th className="px-3 py-3 text-xs font-semibold d28d-text-muted uppercase">Fecha</th>
              <th className="px-3 py-3 text-xs font-semibold d28d-text-muted uppercase">Coach</th>
              <th className="px-3 py-3 text-xs font-semibold d28d-text-muted uppercase">
                {t('d28d.gallery.views', 'Vistas')}
              </th>
              {!readOnly ? (
                <th className="px-3 py-3 text-xs font-semibold d28d-text-muted uppercase">
                  {isGymScoped ? t('common.gym', 'Sede') : t('d28d.gallery.scope', 'Alcance')}
                </th>
              ) : null}
              <th className="px-3 py-3 text-xs font-semibold d28d-text-muted uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((item) => (
              <tr key={item.id} className="border-t border-[var(--d28d-border)] hover:bg-white/5">
                <td className="px-3 py-3">
                  <p className="font-medium text-[var(--d28d-text)]">{item.title}</p>
                  {item.program_id ? (
                    <p className="text-xs d28d-text-muted">
                      {programs.find((p) => String(p.id) === String(item.program_id))?.name || item.program_id}
                    </p>
                  ) : null}
                </td>
                <td className="px-3 py-3 text-sm text-[var(--d28d-text)]">{formatDate(item.class_date)}</td>
                <td className="px-3 py-3 text-sm text-[var(--d28d-text)] normal-case">{formatCoachName(item.coach)}</td>
                <td className="px-3 py-3 text-sm text-[var(--d28d-text)] font-medium">
                  {Number(item.view_count) || 0}
                </td>
                {!readOnly ? (
                  <td className="px-3 py-3 text-xs d28d-text-muted">
                    {isGymScoped
                      ? (resolvedGymName || t('gym.masters.your_gym', 'Tu sede'))
                      : (item.is_global ? 'D28D global' : `Sede #${item.gym_id}`)}
                    {item.live_class_id ? ` · clase #${item.live_class_id}` : ''}
                  </td>
                ) : null}
                <td className="px-3 py-3">
                  <div className="d28d-gallery-actions">
                    <button
                      type="button"
                      className="d28d-gallery-btn d28d-gallery-btn--view"
                      onClick={() => openPreview(item)}
                    >
                      <Eye className="w-3.5 h-3.5" aria-hidden />
                      Ver
                    </button>
                    {!readOnly ? (
                      <>
                        <button
                          type="button"
                          className="d28d-gallery-btn d28d-gallery-btn--edit"
                          onClick={() => startEdit(item)}
                        >
                          <Pencil className="w-3.5 h-3.5" aria-hidden />
                          Editar
                        </button>
                        <button
                          type="button"
                          className="d28d-gallery-btn d28d-gallery-btn--danger"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Trash2 className="w-3.5 h-3.5" aria-hidden />
                          Eliminar
                        </button>
                      </>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={readOnly ? 5 : 6} className="text-center py-8 d28d-text-muted text-sm">
                  {readOnly
                    ? (isGymScoped
                      ? 'Aún no hay grabaciones publicadas para tu gimnasio.'
                      : 'Aún no hay grabaciones publicadas para tu programa.')
                    : (isGymScoped
                      ? 'Aún no hay grabaciones de tu sede. Publica la primera o vincula una clase pasada.'
                      : (isPlatform
                        ? 'Aún no hay grabaciones. Publica la primera o vincula una clase pasada.'
                        : 'Publica la URL de una clase de tu sede para que tus usuarios la vean on-demand.'))}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {previewItem ? <VideoPreviewModal item={previewItem} onClose={() => setPreviewItem(null)} /> : null}
    </div>
  );
}
